<template>
    <div>
        <div class="mb-2">
            <div v-if="motorinStats[0].value > 0" class="stats-group motorin mb-2">
                <h6 class="stats-group-title">Motorin Durumu</h6>
                <div class="row">
                    <div v-for="(stat, index) in motorinStats" :key="`motorin-${index}`" class="col-md-3">
                        <VStatsBox v-bind="stat" />
                    </div>
                </div>
            </div>
            <div v-if="adblueStats[0].value > 0" class="stats-group adblue">
                <h6 class="stats-group-title">AdBlue Durumu</h6>
                <div class="row">
                    <div v-for="(stat, index) in adblueStats" :key="`adblue-${index}`" class="col-md-3">
                        <VStatsBox v-bind="stat" />
                    </div>
                </div>
            </div>
        </div>
        <b-card no-body>
            <v-table ref="storageTable" :items="filteredItems" :fields="tableFields" pagination="client" :search="true" name="FuelStorageStockStatusReport" v-on:sort-changed="handleSort">
                <template #filters>
                    <div class="row align-items-end">
                        <div class="col-md-3 col-6 mb-1">
                            <label>Şube</label>
                            <VSelectList v-model="filters.branchName" :options="branches" label="Name" :reduce="(branch) => branch.Name" placeholder="Şube Seçin..." clearable />
                        </div>
                        <div class="col-md-3 col-6 mb-1">
                            <label>Yakıt Türü</label>
                            <VSelectList v-model="filters.fuelTypeName" :options="fuelTypes" label="VisibleName" :reduce="(fuel) => fuel.VisibleName" placeholder="Yakıt Türü Seçin..." clearable />
                        </div>
                        <div class="col-md-3 col-6 mb-1">
                            <label>Stok Durumu</label>
                            <VSelectList v-model="filters.stockStatus" :options="stockStatusOptions" label="text" :reduce="(status) => status.value" placeholder="Stok Durumu Seçin..." clearable />
                        </div>
                        <div class="col-md-3 col-6 mb-1 d-flex align-items-end">
                            <button class="btn btn-outline-secondary" @click="clearFilters">Temizle</button>
                        </div>
                    </div>
                </template>
                <template #additional-actions>
                    <div class="d-flex justify-content-end">
                        <button v-b-tooltip.hover.bottom title="Dışa Aktar" class="btn btn-sm btn-icon rounded-circle btn-outline-success p-25" @click="downloadExcel">
                            <feather-icon icon="ArrowDownIcon" size="12" />
                        </button>
                    </div>
                </template>
            </v-table>
        </b-card>
    </div>
</template>

<script>
import { BCard } from "bootstrap-vue";
import VStatsBox from "@/components/VStatsBox.vue";
import VSelectList from "@/components/VSelectList.vue";

import FuelStorageApi from "@/api/fuel-storage";
import CompanyApi from "@/api/company";
import SystemDataApi from "@/api/system-data";
import Additional from "@/utilities/additional";

export default {
    name: "FuelStorageStockStatusReportView",
    components: {
        BCard,
        VStatsBox,
        VSelectList,
    },
    data() {
        return {
            sortBy: "FuelStorageCode",
            sortDesc: false,
            allStorages: [],
            branches: [],
            fuelTypes: [],
            stockStatusOptions: [
                { value: "Kritik", text: "Kritik Stok" },
                { value: "Minimum Altında", text: "Minimum Altı" },
                { value: "Normal", text: "Normal" },
            ],
            filters: {
                branchName: null,
                fuelTypeName: null,
                stockStatus: null,
            },
            tableFields: [
                { key: "FuelStorageCode", name: "Depo Kodu", sortable: true, tdClass: "w-5" },
                { key: "FuelStorageName", name: "Depo Adı", sortable: false, tdClass: "w-10" },
                { key: "BranchName", name: "Şube", sortable: false, tdClass: "w-10" },
                { key: "FuelTypeName", name: "Yakıt Türü", sortable: false, tdClass: "w-10" },
                { key: "CapacityStr", name: "Kapasite [lt]", sortable: true, tdClass: "text-right w-10" },
                { key: "MinStockLevelStr", name: "Min. Stok [lt]", sortable: false, tdClass: "text-right w-10" },
                { key: "CriticalStockLevelStr", name: "Kritik Stok [lt]", sortable: false, tdClass: "text-right w-10" },
                { key: "CurrentStockStr", name: "Mevcut Stok [lt]", sortable: false, tdClass: "text-right w-8 font-weight-bolder" },
                {
                    key: "CurrentStockPercentageStr",
                    name: "Doluluk Oranı [%]",
                    sortable: true,
                    tdClass: "text-center w-18",
                    component: true,
                    componentOptions: { name: "fuel-storage/StorageFillRate" },
                },
                { key: "StockStatus", name: "Durum", sortable: true, tdClass: "text-center w-10", component: true, componentOptions: { name: "fuel-storage/StorageStatusBadge" } },
            ],
        };
    },
    computed: {
        filteredItems() {
            let items = [...this.allStorages];

            if (this.filters.branchName) {
                items = items.filter((item) => item.BranchName === this.filters.branchName);
            }
            if (this.filters.fuelTypeName) {
                const filterFuelType = this.filters.fuelTypeName.toLocaleLowerCase("tr");
                items = items.filter((item) => item.FuelTypeName.toLocaleLowerCase("tr") === filterFuelType);
            }
            if (this.filters.stockStatus) {
                items = items.filter((item) => item.StockStatus === this.filters.stockStatus);
            }
            if (this.sortBy) {
                items.sort((a, b) => {
                    let valA = a[this.sortBy];
                    let valB = b[this.sortBy];

                    if (valA === null) return 1;
                    if (valB === null) return -1;

                    if (typeof valA === "string") {
                        return valA.localeCompare(valB, "tr") * (this.sortDesc ? -1 : 1);
                    }
                    return (valA - valB) * (this.sortDesc ? -1 : 1);
                });
            }

            return items;
        },
        summaryStats() {
            return this.generateSummaryStats(this.filteredItems);
        },
        motorinStats() {
            const motorinData = this.filteredItems.filter((item) => item.FuelTypeName.toLocaleLowerCase("tr") === "motorin");
            return this.generateSummaryStats(motorinData);
        },
        adblueStats() {
            const adblueData = this.filteredItems.filter((item) => item.FuelTypeName.toLocaleLowerCase("tr") === "adblue");
            return this.generateSummaryStats(adblueData);
        },
    },
    created() {
        this.loadFilterData();
        this.fetchAllData();
    },
    methods: {
        generateSummaryStats(data, fuelTypeName = null) {
            if (!data || data.length === 0) {
                const titleSuffix = fuelTypeName ? ` (${fuelTypeName})` : "";
                return [
                    { text: `Toplam Depo Sayısı${titleSuffix}`, value: 0, icon: "inventory_2", variant: "primary" },
                    { text: "Toplam Kapasite [lt]", value: "0", icon: "oil_barrel", variant: "success" },
                    { text: "Mevcut Stok [lt]", value: "0", icon: "opacity", variant: "info" },
                    { text: "Doluluk Oranı [%]", value: "0,0", icon: "speed", variant: "warning" },
                ];
            }

            const totalCapacity = data.reduce((sum, s) => sum + s.Capacity, 0);
            const totalStock = data.reduce((sum, s) => sum + s.CurrentStock, 0);
            const occupancyRate = totalCapacity > 0 ? (totalStock / totalCapacity) * 100 : 0;
            const titleSuffix = fuelTypeName ? ` (${fuelTypeName})` : "";

            return [
                { text: `Toplam Depo Sayısı${titleSuffix}`, value: data.length, icon: "inventory_2", variant: "primary" },
                { text: "Toplam Kapasite [lt]", value: totalCapacity.toLocaleString("tr-TR"), icon: "oil_barrel", variant: "success" },
                { text: "Mevcut Stok [lt]", value: totalStock.toLocaleString("tr-TR"), icon: "opacity", variant: "info" },
                { text: "Doluluk Oranı [%]", value: occupancyRate.toLocaleString("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), icon: "speed", variant: "warning" },
            ];
        },
        handleSort({ sortBy, sortDesc }) {
            this.sortBy = sortBy;
            this.sortDesc = sortDesc;
        },
        fetchAllData() {
            FuelStorageApi.FuelStorageReport.GetFuelStorageStockLevelReport({})
                .then((response) => {
                    this.allStorages = response.data.Data || [];
                    // this.calculateSummaryStats(this.allStorages);
                })
                .catch(() => {
                    this.$toast.error("Rapor verileri alınırken bir hata oluştu.");
                });
        },
        loadFilterData() {
            CompanyApi.Branch.ListByCode("O").then((res) => {
                this.branches = res.data.Data || [];
            });
            SystemDataApi.FuelType(true).then((res) => {
                this.fuelTypes = res.data.Data || [];
            });
        },
        calculateSummaryStats(data) {
            if (!data || data.length === 0) return;

            const totalCapacity = data.reduce((sum, s) => sum + s.Capacity, 0);
            const totalStock = data.reduce((sum, s) => sum + s.CurrentStock, 0);

            this.summaryStats[0].value = data.length;
            this.summaryStats[1].value = totalCapacity.toLocaleString("tr-TR");
            this.summaryStats[2].value = totalStock.toLocaleString("tr-TR");
            this.summaryStats[3].value = totalCapacity > 0 ? ((totalStock / totalCapacity) * 100).toFixed(1) : 0;
        },
        clearFilters() {
            this.filters = {
                branchName: null,
                fuelTypeName: null,
                stockStatus: null,
            };
        },
        downloadExcel() {
            const payload = {
                BranchName: this.filters.branchName,
                FuelTypeName: this.filters.fuelTypeName,
                StockStatus: this.filters.stockStatus,
            };

            FuelStorageApi.ExcelReport.ExportFuelStorageStockStatusReport(payload)
                .then((res) => {
                    if (res.data.Success) {
                        const excelPath = res.data.Data;
                        Additional.DownloadTab(excelPath);
                        this.$toast.success("Excel raporu başarıyla indirildi.");
                    } else {
                        this.$toast.error(res.data.Message || "Excel raporu oluşturulurken bir hata oluştu.");
                    }
                })
                .catch(() => {
                    this.$toast.error("Excel raporu oluşturulurken bir hata oluştu.");
                });
        },
    },
};
</script>
<style>
.stats-group {
    border: 1px solid #e9ecef;
    border-left-width: 4px;
    border-radius: 6px;
    padding: 1rem;
}
.stats-group.motorin {
    border-left-color: #ffc107;
}
.stats-group.adblue {
    border-left-color: #0d6efd;
}
.stats-group-title {
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: 600;
    margin-top: -0.25rem;
    margin-bottom: 0.75rem;
}
</style>
